// One governed capture action across Design V2 customer surfaces.
import React, { useCallback, useEffect, useRef, useState } from "react";

const ENABLED = process.env.REACT_APP_OPERATIONAL_CAPTURE_UI_ENABLED === "1";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const validId = (id) => UUID_RE.test(String(id || ""));
const STREAMS = ["aws_s3_posture", "aws_iam_inventory", "aws_cloudtrail_events", "aws_config_resources", "aws_ec2_inventory"];

export default function OperationalCaptureAction({ label, apiBaseUrl, getAccessToken, tenantId = "", userId = "", connectorId = "", onAccepted, onCompleted, className = "btn btn-sm", discoverConnector = false, renderButton }) {
  const [discoveredId, setDiscoveredId] = useState("");
  const [availableConnectors, setAvailableConnectors] = useState([]);
  const [loadingConnectors, setLoadingConnectors] = useState(discoverConnector);
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [message, setMessage] = useState("");
  const [jobs, setJobs] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [captureGroup, setCaptureGroup] = useState(null);
  const completedCaptureIdsRef = useRef(new Set());
  const [statusError, setStatusError] = useState("");
  const activeRef = useRef(false);
  const stateRef = useRef("idle");
  const base = String(apiBaseUrl || "").replace(/\/$/, "");
  const selectedId = validId(connectorId) ? connectorId : (validId(discoveredId) ? discoveredId : "");
  const request = useCallback(async (path, body, method = "POST") => {
    const token = await getAccessToken();
    if (!token) throw new Error("Authentication token unavailable");
    const response = await fetch(`${base}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
        ...(userId ? { "X-User-Id": userId } : {}),
      },
      ...(method === "POST" ? { body: JSON.stringify(body || {}) } : {}),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}: capture service unavailable or not authorized`);
    return response.json();
  }, [base, getAccessToken, tenantId, userId]);

  useEffect(() => {
    if (!ENABLED || !discoverConnector || validId(connectorId)) return undefined;
    let cancelled = false;
    setLoadingConnectors(true);
    setDiscoveredId("");
    setAvailableConnectors([]);
    setMessage("");
    request("/operational-capture/connectors", undefined, "GET")
      .then((data) => {
        if (cancelled) return;
        const matches = (Array.isArray(data?.connectors) ? data.connectors : [])
          .filter((c) => validId(c?.connector_id));
        setAvailableConnectors(matches);
        if (matches.length === 1) setDiscoveredId(matches[0].connector_id);
        else if (matches.length === 0) setMessage("No connected AWS account is available for this organization. Connect AWS in Admin → Connections.");
        else setMessage("Choose which AWS account to capture.");
      })
      .catch((error) => {
        if (!cancelled) setMessage(`AWS connection lookup failed: ${String(error?.message || error)}`);
      })
      .finally(() => { if (!cancelled) setLoadingConnectors(false); });
    return () => { cancelled = true; };
  }, [connectorId, discoverConnector, request]);

  const refreshCaptureFacts = useCallback(async () => {
    if (!selectedId) return;
    const [latest, summary] = await Promise.all([
      request("/operational-capture/latest", { connector_id: selectedId }),
      request("/operational-capture/evidence-summary", { connector_id: selectedId }),
    ]);
    setJobs(Array.isArray(latest.jobs) ? latest.jobs : []);
    const group = latest?.capture_group || null;
    setCaptureGroup(group);
    stateRef.current = group?.operation_state || "idle";
    if (group?.operation_state !== "running" && group?.operation_state !== "cancellation_requested") {
      setConfirmCancel(false);
    }
    // Customer data must refresh only after successful jobs have durable
    // publications (or failed jobs have reached terminal collection). Collection
    // completion alone is not publication or assessment completion.
    const captureId = String(group?.capture_id || latest?.capture_id || "");
    if (
      captureId &&
      group?.terminal_collection === true &&
      group?.terminal_publication === true &&
      group?.contribution_handoff_settled === true &&
      Number(group?.services_failed) === 0 &&
      Number(group?.services_total) === 5 &&
      Number(group?.services_collected) === 5 &&
      !completedCaptureIdsRef.current.has(captureId)
    ) {
      completedCaptureIdsRef.current.add(captureId);
      onCompleted?.({
        capture_id: captureId,
        capture_collection_state: group.capture_collection_state || null,
        services_total: group.services_total ?? null,
        services_collected: group.services_collected ?? null,
        services_failed: group.services_failed ?? null,
        services_published: group.services_published ?? null,
        terminal_publication: true,
        assessment_sources_synced: group.assessment_sources_synced === true,
        control_contributions_staged: group.control_contributions_staged === true,
        contribution_handoff_settled: group.contribution_handoff_settled === true,
        services_contribution_receipts_staged: group.services_contribution_receipts_staged ?? null,
        // Evidence contribution staging is not control-effectiveness evaluation.
        // Do not assert assessment completion or assurance certification.
      });
    }
    setReceipts(Array.isArray(summary.receipts) ? summary.receipts : []);
    setStatusError("");
    setMessage((previous) => previous.startsWith("Submitting governed collection;") ? "" : previous);
  }, [request, selectedId, onCompleted]);

  useEffect(() => {
    if (!ENABLED || !selectedId) return undefined;
    let cancelled = false;
    const check = async () => {
      try {
        // Sources are persistent, tenant/connector-scoped backend records.
        if (!cancelled) await refreshCaptureFacts();
      } catch (error) {
        if (!cancelled) setStatusError(`Capture status unavailable: ${String(error?.message || error)}`);
      }
    };
    check();
    // Only the small capture-status API is polled, and only while an operation
    // is active. Never poll the dashboard or trigger a full posture refresh.
    const interval = window.setInterval(() => {
      if (!cancelled && !document.hidden &&
          ["running", "cancellation_requested"].includes(stateRef.current)) check();
    }, 20000);
    const onCompletion = (event) => {
      if (!cancelled && event?.detail?.capture_id) check();
    };
    // Event-driven completion stays primary; also reconcile after a tab regains
    // focus or visibility, so a missed event cannot leave a stale busy button.
    const onFocus = () => { if (!cancelled) check(); };
    const onVisibility = () => { if (!document.hidden && !cancelled) check(); };
    window.addEventListener("oasys:capture-complete", onCompletion);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("oasys:capture-complete", onCompletion);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [selectedId, refreshCaptureFacts]);

  if (!ENABLED) return null;
  const publishedAndSettled = captureGroup?.operation_state === "published" &&
    captureGroup?.terminal_collection === true &&
    captureGroup?.terminal_publication === true &&
    captureGroup?.contribution_handoff_settled === true &&
    captureGroup?.control_contributions_staged === true &&
    Number(captureGroup?.services_failed) === 0 &&
    Number(captureGroup?.services_collected) === Number(captureGroup?.services_total) &&
    Number(captureGroup?.services_total) === STREAMS.length;
  const operationState = captureGroup?.operation_state || "idle";
  const cancellationRequested = operationState === "cancellation_requested";
  const cancelled = operationState === "cancelled";
  const needsAttention = operationState === "needs_attention" ||
    (!cancelled && !cancellationRequested &&
      (captureGroup?.capture_collection_state === "incomplete" ||
        (captureGroup?.terminal_collection === true && !publishedAndSettled)));
  const running = captureGroup
    ? operationState === "running" || cancellationRequested
    : jobs.some(j => ["queued", "running", "pending"].includes(String(j.collection_status || "").toLowerCase()));
  const pending = busy || (running && !cancellationRequested);
  const cancelCapture = async () => {
    const captureId = captureGroup?.capture_id;
    if (!selectedId || !validId(captureId) || cancelling) return;
    setCancelling(true);
    setConfirmCancel(false);
    setMessage("Requesting capture cancellation…");
    try {
      const result = await request("/operational-capture/cancel-request", {
        connector_id: selectedId, capture_id: captureId,
      });
      setMessage(result?.status === "already_terminal"
        ? "Capture already finished; cancellation was not applied."
        : "Cancellation requested; waiting for active workers to stop.");
      await refreshCaptureFacts();
    } catch (error) {
      setMessage(`Cancellation request failed: ${String(error?.message || error)}`);
    } finally {
      setCancelling(false);
    }
  };
  const capture = async () => {
    if (!selectedId) {
      setMessage("Select a connected AWS account before capturing evidence.");
      return;
    }
    if (activeRef.current || running) return;
    activeRef.current = true;
    setBusy(true);
    setMessage("Submitting governed five-service collection…");
    try {
      const result = await request("/operational-capture/now", { connector_id: selectedId });
      setJobs(Array.isArray(result.jobs) ? result.jobs : []);
      setCaptureGroup(null);
      setMessage("Submitting governed collection; checking service results…");
      // Submission is not completion; periodic status refresh follows the durable jobs.
      refreshCaptureFacts().catch(() => {});
      onAccepted?.(result);
    } catch (error) {
      setMessage(String(error?.message || error));
    } finally {
      activeRef.current = false;
      setBusy(false);
    }
  };
  const disabled = busy || running || cancelling || !selectedId || loadingConnectors;
  const captureSummary = (() => {
    const latest = jobs.filter((j) => STREAMS.includes(j.stream));
    const active = latest.filter((j) => ["queued", "running", "pending"].includes(String(j.collection_status || "").toLowerCase()));
    const failed = latest.filter((j) => ["failed", "error", "cancelled", "canceled", "dead_letter"].includes(String(j.collection_status || "").toLowerCase()));
    const collected = latest.filter((j) => String(j.collection_status || "").toLowerCase() === "success");
    // Never derive a five-job completion time from unrelated latest-per-stream receipts.
    const latestPublishedAt = captureGroup?.last_evidence_published_at || null;
    // The latest-per-stream job view is not a five-job batch receipt. Never declare
    // an entire batch complete or all five streams published from this aggregate.
    return { active, failed, collected, latestPublishedAt, latest };
  })();
  const formatTime = (value) => {
    if (!value || Number.isNaN(Date.parse(value))) return "Not verified";
    return new Date(value).toLocaleString();
  };
  const animatedLabel = cancellationRequested ? "Cancellation requested" : pending ? (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span>{busy ? "Submitting evidence" : "Capturing evidence"}</span>
      <img
        src="/oasys-auth-logo-v2.png"
        alt=""
        aria-hidden="true"
        className="oasys-capture-logo"
      />
    </span>
  ) : loadingConnectors ? "Loading AWS connection…" : label;
  return (
    <div className="oasys-capture-action" style={{
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "flex-start",
      position: "relative",
      flex: "0 0 auto",
    }}>
      <style>{`

        .oasys-capture-logo {
          width: 24px;
          height: 24px;
          object-fit: contain;
          flex-shrink: 0;
          animation: oasysLogoPulse 1.8s ease-in-out infinite;
        }
        @keyframes oasysLogoPulse {
          0%, 100% {
            opacity: 0.65;
            filter: brightness(0.85);
            transform: scale(0.96);
          }
          50% {
            opacity: 1;
            filter: brightness(1.25);
            transform: scale(1.04);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .oasys-capture-logo {
            animation: none;
          }
        }
      `}</style>
      {renderButton ? renderButton({ onClick: capture, disabled, label: animatedLabel }) : (
        <button type="button" className={className} onClick={capture} disabled={disabled}>
          {animatedLabel}
        </button>
      )}
      {confirmCancel && (
        <div role="dialog" aria-modal="true" aria-labelledby="oasys-cancel-title"
          style={{ position: "fixed", inset: 0, zIndex: 11000, background: "rgba(0,0,0,.64)", display: "grid", placeItems: "center", padding: 16 }}>
          <div style={{ background: "#18212e", color: "#fff", borderRadius: 16, padding: 24, maxWidth: 420, width: "100%", boxShadow: "0 16px 40px rgba(0,0,0,.4)" }}>
            <h3 id="oasys-cancel-title" style={{ marginTop: 0 }}>Cancel evidence capture?</h3>
            <p>Unfinished collection jobs will be asked to stop. Previously published evidence will be preserved. Any AWS operation already in progress may need to finish before cancellation is complete.</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" className="btn btn-sm" onClick={() => setConfirmCancel(false)} autoFocus>Keep Capturing</button>
              <button type="button" className="btn btn-sm btn-danger" onClick={cancelCapture} disabled={cancelling}>Confirm Cancel</button>
            </div>
          </div>
        </div>
      )}
      <div style={{
        position: "absolute",
        top: "calc(100% + 8px)",
        left: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 4,
        minWidth: "max-content",
        maxWidth: "min(420px, 90vw)",
        zIndex: 1,
      }}>
      {/* Secondary actions stay outside the primary button's layout height.
          This keeps Capture Evidence Now aligned with Refresh View and Open Posture. */}
      {running && !cancellationRequested && validId(captureGroup?.capture_id) && (
        <button type="button" className="btn btn-sm" onClick={() => setConfirmCancel(true)}
          disabled={cancelling || busy} style={{ color: "#dc3545", order: 0 }}>
          Cancel Capture
        </button>
      )}
      {discoverConnector && availableConnectors.length > 1 && (
        <label style={{ fontSize: 12, order: 2 }}>
          AWS connection
          <select aria-label="AWS connection" value={discoveredId} onChange={(event) => { setDiscoveredId(event.target.value); setJobs([]); setCaptureGroup(null); setReceipts([]); setMessage(""); }}>
            <option value="">Select AWS connection</option>
            {availableConnectors.map((c) => <option key={c.connector_id} value={c.connector_id}>{c.connector_id}</option>)}
          </select>
        </label>
      )}
      {message && <span role="status" style={{ fontSize: 12, maxWidth: 350, order: 3 }}>{message}</span>}
      {selectedId && (
        <div className="oasys-capture-compact-r2" role="region" aria-label="AWS evidence collection"
          style={{ fontSize: 12, maxWidth: 420, lineHeight: 1.5, order: 1, textAlign: "left" }}>
          {captureGroup && cancellationRequested && (
            <div role="status">Cancellation requested. Waiting for active collection jobs to stop.</div>
          )}
          {captureGroup && running && !cancellationRequested && (
            <div role="status">Evidence collection in progress.</div>
          )}
          {captureGroup && cancelled && (
            <div role="status">Capture cancelled. Previously published evidence is retained.</div>
          )}
          {captureGroup && needsAttention && !running && (
            <div role="alert">Capture needs attention. Review Mission Execution for details.</div>
          )}
          {publishedAndSettled && captureSummary.latestPublishedAt && (
            <div>Last evidence published: {formatTime(captureSummary.latestPublishedAt)}</div>
          )}
          {statusError && <span role="alert">Collection status unavailable. Please refresh.</span>}
        </div>
      )}
      </div>
    </div>
  );
}
